<script>
	import Popover from './popover.svelte'
	import { onMount } from 'svelte'
  import { Turbo } from "@hotwired/turbo-rails"
  import debounce from 'debounce'
  import { LoaderCircle, Ellipsis } from 'lucide-svelte'

	let {
    open = $bindable(false),
    value = $bindable(null),
    items = $bindable([]),
    query = $bindable(""),
    labelKey = "label",
    subtitleKey = null,
    valueKey = "value",
    pathKey = "path",
    queryKey = "q",
    placeholder = "Rechercher...",
    load,
    url,
    initialUrl,
    name,
    loadAll = false,
    fetchInitial = true,
    onValueChange,
    onClear,
    onSelect,
    onResults,
    onFocus,
    onBlur,
    showList = true,
    navigateOnSelect = null,
    itemTemplate,
    filter = true,
    ...inputProps
  } = $props()

	let index = $state(0)
  let typing = $state(false)
  let loading = $state(false)
  let navigating = $state(false)
  let itemRefs = $state([])
  let inputRef = $state(null)
  let fetchSequence = 0

  const debouncedLoad = load ? debounce(load, 300) : null
  const debouncedFetch = url ? debounce(fetchFromUrl, 300) : null

	onMount(() => {
    async function initialize() {
      if (url && loadAll) {
        await fetchFromUrl({ includeQuery: false, includeIds: false })
      } else if (initialUrl) {
        await fetchFromUrl({ includeQuery: false, initial: true })
      } else if (url && fetchInitial) {
        await fetchFromUrl({ includeQuery: false, includeIds: true })
      }

      // selectedItem && selectItem(selectedItem)
      if (selectedItem) {
        selectItem(selectedItem, null, { triggerCallback: false })
        query = selectedItem[labelKey]
      }
    }

    initialize()
	})

  // An effect that smooth scrolls the highlighted item into view
  $effect(() => {
    if (itemRefs[index]) {
      itemRefs[index]?.scrollIntoView({ behavior: "instant", block: "nearest" })
    }
  })

  // An effect that runs when the value changes
  $effect(() => {
    if (value) {
      query = items.find(item => item[valueKey] === value)?.[labelKey] ?? ""
    }
  })

	let filteredItems = $derived.by(() => {
    if (!filter || url || load) return items
		return items.filter((item) => {
			return item[labelKey].toLowerCase().includes(query.toLowerCase())
		})
	})

	let highlightedItem = $derived(filteredItems[index])
	let selectedItem = $derived(value && items.find(item => item[valueKey] === value))

  async function fetchFromUrl({ includeQuery = true, includeIds = false, initial = false } = {}) {
    typing = false
    if (!url || (!query.length && (!loadAll && !fetchInitial))) return
    const requestId = ++fetchSequence
    const requestedQuery = query
    loading = true
    const isAbsoluteUrl = url.startsWith("http")
    const baseUrl = initial ? initialUrl : url
    const fetchUrl = isAbsoluteUrl ? new URL(baseUrl) : new URL([window.location.origin, baseUrl].join('/'))
    if (includeQuery) fetchUrl.searchParams.set(queryKey, query)
    if (includeIds) fetchUrl.searchParams.append("ids[]", value)
    try {
      const response = await fetch(fetchUrl.toString())
      const data = await response.json()
      if (requestId !== fetchSequence || (includeQuery && requestedQuery !== query)) return
      items = data
      onResults?.(data)
    } catch (error) {
      if (requestId !== fetchSequence) return
      console.error(error)
      items = []
    } finally {
      if (requestId === fetchSequence) loading = false
    }
  }

  function handleUpKey() {
    index = Math.max(index - 1, 0)
  }

  function handleDownKey() {
    if (!open) return open = true
    index = Math.min(index + 1, items.length - 1)
  }

  function handleEnterKey(e) {
    e.preventDefault()
    e.stopPropagation()
    if (Boolean(highlightedItem)) {
      const selected = handleSelect()
      if (selected) {
        closeList()
      }
    }
  }

  function handleEscapeKey() {
    closeList()
  }

	function handleKeydown(e) {
    if (e.key === "ArrowUp") {
      handleUpKey()
    } else if (e.key === "ArrowDown") {
      handleDownKey()
    } else if (e.key === "Enter") {
      handleEnterKey(e)
    } else if (e.key === "Escape") {
      handleEscapeKey()
    }
	}

	async function handleInput(e) {
    if (navigating) return
    index = 0
    open = true
    if (selectedItem && query !== selectedItem[labelKey]) {
      value = null
    }
		if (!query.length) {
      fetchSequence += 1
      value = null
      items = []
      typing = false
      loading = false
      onValueChange?.(null)
      onClear?.()
      return
    }
    if (url && !loadAll) {
      typing = true
      await debouncedFetch()
    }
    if (load) await debouncedLoad()
	}

	function handleSelect() {
		if (highlightedItem && !highlightedItem.disabled) {
			return selectItem(highlightedItem)
		}
		return false
	}

	function selectItem(item, e, { triggerCallback = true } = {}) {
		e?.preventDefault()
		e?.stopPropagation()
    if (item.disabled || navigating) return false
		value = item[valueKey]
		query = item[labelKey]
    closeList()
    if (triggerCallback) onValueChange?.(item)
    const handled = onSelect?.(item)
    if (handled === false) return true
    navigateToItem(item)
    return true
  }

  function navigateToItem(item) {
    const path = item?.[pathKey]
    const shouldNavigate = navigateOnSelect ?? Boolean(path)
    if (!shouldNavigate || !path) return

    navigating = true
    Turbo.visit(path)
  }

  function closeList() {
    open = false
    index = 0
  }

  function handleWindowPointerDown(e) {
    // This won't overwrite successful item selection but should stop all other "clicks"
    if (e.target.closest(".autocomplete-list")) {
      e.preventDefault()
      e.stopImmediatePropagation()
    }
  }

  export function addItems(newItems) {
    items = [...items, ...newItems]
  }

  export function setValue(item, { triggerCallback = false } = {}) {
    value = item[valueKey]
    query = item[labelKey]
    if (triggerCallback) onValueChange?.(item)
  }

  export function clearValue(triggersCallback = true) {
    value = null
    query = ""
    if (triggersCallback) onValueChange?.(value)
  }

  export function setQuery(q) {
    query = q
  }

  export function clearQuery() {
    query = ""
  }

  export function focus() {
    // open = true
    inputRef?.focus()
  }

</script>

<svelte:window onpointerdown={handleWindowPointerDown} />

<Popover bind:open placement="bottom-start">

  {#snippet trigger()}
    <div class="input-container border border-gray-200 relative focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
      <input
        bind:this={inputRef}
        bind:value={query}
        onfocus={() => {
          if (navigating) return
          open = true
          onFocus?.()
        }}
        onclick={() => {
          if (!navigating) open = true
        }}
        onblur={() => {
          closeList()
          onBlur?.()
        }}
        onkeydown={handleKeydown}
        oninput={handleInput}
        class="w-full text-gray-600 text-smaller focus:outline-none"
        name={name}
        autocomplete="off"
        {placeholder}
        {...inputProps}
        disabled={navigating || inputProps.disabled}
      />
      {#if loading || typing || navigating}
        <div class="loading">
          {#if loading || navigating}
            <LoaderCircle size={16} />
          {:else}
            <Ellipsis size={16} />
          {/if}
        </div>
      {/if}
    </div>
	{/snippet}

  {#snippet contents({ maxHeight })}
    {@const preferredMaxHeight = Math.min(maxHeight, 300) }

    {#if showList && (query.length > 0 || filteredItems.length > 0)}
      <div class="autocomplete-list" style={`max-height: ${preferredMaxHeight}px`}>
        {#if loading}
          <div class="item informational">Chargement...</div>
        {:else if navigating}
          <div class="item informational">Ouverture...</div>
        {:else if typing}
          <div class="item informational">Recherche en cours...</div>
        {:else if filteredItems.length > 0}
          {#each filteredItems as item, idx (item[valueKey])}
            {#if itemTemplate}
              {@render itemTemplate(item, selectItem)}
            {:else}
              <div
                bind:this={itemRefs[idx]}
                class="item"
                class:highlighted={idx === index}
                class:selected={item[valueKey] === value}
                class:disabled={item.disabled}
                onpointerdown={(e) => selectItem(item, e)}
              >
                <span class="item-content">
                  <span class="item-title">{item[labelKey]}</span>
                  {#if subtitleKey && item[subtitleKey]}
                    <span class="item-subtitle">{item[subtitleKey]}</span>
                  {/if}
                </span>
                {#if item[pathKey]}
                  <i class="fal fa-external-link-alt item-path-icon" aria-hidden="true"></i>
                {/if}
              </div>
            {/if}
          {:else}
            <div class="item informational">{items.length ? "Aucun résultat" : "Aucun élément disponible"}</div>
          {/each}
        {:else}
          <div class="item informational">Aucun résultat</div>
        {/if}
      </div>
    {/if}
	{/snippet}
</Popover>

{#if name}
  <input type="hidden" name={name} value={value ?? ""} />
{/if}

<style>
	.autocomplete-list {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border-radius: 0.5rem;
    padding: 0.4rem 0;
      border: 1px solid #e0e0e0;
    background: white;
    overflow-y: auto;
    width: 100%;
    &.empty {
      padding: 0;
      border: none;
      box-shadow: none;
    }
	}
	.item {
		background: white;
		color: #374151;
		padding: 0.5rem 0.85rem;
		font-size: 0.8rem;
    border-bottom: 1px solid #f3f4f6;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    &:last-child {
      border-bottom: none;
    }
    &:hover {
      background: #f3f4f6;
    }
		&.highlighted {
			background: #eff6ff;
			color: #1f2937;
		}
    &.selected {
      background: #eff6ff;
      color: #1f2937;
    }
    &.informational {
      color: #888;
      pointer-events: none;
    }

    &.disabled {
      opacity: 0.5;
      /* cursor: not-allowed; */
      pointer-events: none;
    }
	}

  .item-content {
    min-width: 0;
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.15rem;
  }

  .item-title {
    line-height: 1.25;
  }

  .item-subtitle {
    color: #9ca3af;
    font-size: 0.72rem;
    line-height: 1.2;
  }

  .item-path-icon {
    color: #cbd5e1;
    flex: 0 0 auto;
    font-size: 0.72rem;
  }

  .loading {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7280;
  }

  input:disabled {
    cursor: wait;
    opacity: 0.75;
  }
</style>
